import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "@/lib/api";
import { GUESTBOOK } from "@/constants/testIds";
import { Wifi, LogOut, MapPin, Shield, Phone, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { GuestDarkToggle, useGuestDarkMode } from "@/hooks/useGuestDarkMode";

const BASE = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, label, testId, children }) => (
  <section data-testid={testId} className="ns-card p-6 md:p-8">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="ns-label">{label}</div>
    </div>
    <div className="text-slate-700 whitespace-pre-line leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function GuestBook() {
  const { slug } = useParams();
  // Session Z81/Z82 — guest-side dark mode persisted via a server-signed
  // cookie so a flip survives magic-link visits and devices.
  useGuestDarkMode();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${BASE}/api/public/guestbook/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-8">
        <div className="ns-card p-10 text-center max-w-md">
          <div className="font-serif text-3xl">Guest book not available</div>
          <p className="text-sm text-slate-500 mt-3">
            This link may have been retired by your host. Please reach out to Newport Stays for help.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-slate-400 text-sm tracking-widest uppercase">Loading…</div>
      </div>
    );
  }

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div data-testid={GUESTBOOK.root} className="min-h-screen bg-stone-50">
      <div
        className="h-64 md:h-80 relative bg-slate-800"
        style={
          data.hero_image_url
            ? {
                backgroundImage: `url(${data.hero_image_url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
            : undefined
        }
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />
        <div className="absolute top-4 right-4 z-10">
          <GuestDarkToggle className="!bg-white/10 !border-white/20 !text-white hover:!bg-white/20 hover:!border-white/40 hover:!text-white" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 h-full flex flex-col justify-end pb-8 text-white">
          <div className="flex items-center gap-3 mb-6">
            <img src={LOGO_URL} alt="" className="w-10 h-10 rounded-lg ring-1 ring-white/30" />
            <div className="text-[11px] tracking-[0.24em] uppercase text-teal-200/90">
              Newport Stays · Guest book
            </div>
          </div>
          <h1 className="font-serif text-4xl md:text-5xl leading-tight drop-shadow">
            Welcome to {data.property_name}
          </h1>
          {data.address && (
            <p className="mt-2 text-white/80 text-sm">{data.address}</p>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-5">
        {(data.wifi_ssid || data.wifi_password) && (
          <Section icon={Wifi} label="Wi-Fi" testId={GUESTBOOK.wifi}>
            {data.wifi_ssid && (
              <div className="flex items-center gap-3 mb-2">
                <span className="text-slate-500 text-sm w-24">Network</span>
                <span className="font-mono">{data.wifi_ssid}</span>
                <button
                  onClick={() => copy(data.wifi_ssid, "Wi-Fi network")}
                  className="text-slate-300 hover:text-teal-700"
                  title="Copy"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}
            {data.wifi_password && (
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm w-24">Password</span>
                <span className="font-mono">{data.wifi_password}</span>
                <button
                  onClick={() => copy(data.wifi_password, "Wi-Fi password")}
                  className="text-slate-300 hover:text-teal-700"
                  title="Copy"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            )}
          </Section>
        )}

        {data.checkout_instructions && (
          <Section icon={LogOut} label="Check-out" testId={GUESTBOOK.checkout}>
            {data.checkout_instructions}
          </Section>
        )}

        {data.local_recommendations && (
          <Section icon={MapPin} label="Local recommendations" testId={GUESTBOOK.recs}>
            {data.local_recommendations}
          </Section>
        )}

        {data.house_rules && (
          <Section icon={Shield} label="House rules" testId={GUESTBOOK.rules}>
            {data.house_rules}
          </Section>
        )}

        {data.emergency_contact && (
          <Section icon={Phone} label="Emergency contact" testId={GUESTBOOK.emergency}>
            {data.emergency_contact}
          </Section>
        )}

        {data.cleaner_whatsapp && (
          <a
            data-testid="guestbook-cleaner-whatsapp"
            href={`https://wa.me/${data.cleaner_whatsapp.replace(/[^+\d]/g, "").replace(/^\+/, "")}?text=${encodeURIComponent(`Hi ${data.cleaner_name || "team"}, I'm staying at ${data.property_name} — `)}`}
            target="_blank"
            rel="noreferrer"
            className="ns-card p-5 flex items-center justify-between gap-4 hover:border-emerald-300 hover:shadow-lg transition-all"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <div className="ns-label text-emerald-700">Message the cleaner on WhatsApp</div>
                <div className="text-sm text-slate-600 mt-1 truncate">
                  Any question — heating, laundry, checkout — {data.cleaner_name || "the team"} will reply here.
                </div>
              </div>
            </div>
            <div className="text-xs text-emerald-700 font-medium whitespace-nowrap">Open →</div>
          </a>
        )}

        <div className="text-center text-[11px] tracking-widest uppercase text-slate-400 pt-8">
          Newport Stays · Serviced Accommodation
        </div>
      </div>
    </div>
  );
}
